import React, { useEffect, useState } from 'react';
import { api } from '../../../api/apiProvider';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, Flex, Form, message, Radio } from 'antd';
import urls from '../../../api/urls';
import { t } from 'i18next';
import Roles from '../../../roles/Roles';
import usePeriods from './forecasts/usePeriods';
import ButtonBlack from '../../ui/buttons/ButtonBlack';
import { Engine } from '../../../api/types';
import HalfYearPicker from '../../ui/HalfYearPicker';

const { RangePicker } = DatePicker;

dayjs.extend(quarterOfYear);

interface PeriodFormProps {
  engine: Engine;
}

const dateFormat = 'YYYY-MM-DD';

const addPeriod = async (data: any) => {
  return await api.post(`${urls.periods}`, { arg: data });
};

export default function PeriodForm({ engine }: PeriodFormProps) {
  const [form] = Form.useForm();
  const [isPeriodCreating, setIsPeriodCreating] = useState(false);
  const [periodType, setPeriodType] = useState<'custom' | 'quarter' | 'semester'>('quarter');

  const { mutatePeriods } = usePeriods(engine.id);

  const today = dayjs().startOf('day');
  const isWeekInterval = engine.isWeekInterval;

  useEffect(() => {
    form.resetFields();
  }, [periodType, form]);

  const createPeriod = async () => {
    if (!Roles.hasAccess('canCreateEnginePeriod')) return;

    try {
      const values = await form.validateFields();

      setIsPeriodCreating(true);

      let dateStart: string;
      let dateEnd: string;
      let periodLength = 0;

      if (periodType === 'custom') {
        const range = values.period as [Dayjs, Dayjs];
        dateStart = range[0].format(dateFormat);
        dateEnd = range[1].format(dateFormat);
        periodLength = 1;
      } else if (periodType === 'quarter') {
        const date = values.period as Dayjs;
        dateStart = date.startOf('quarter').format(dateFormat);
        dateEnd = date.endOf('quarter').format(dateFormat);
      } else {
        const date = values.period as Dayjs;
        const isFirstHalf = date.month() < 6;

        if (isFirstHalf) {
          dateStart = date.startOf('year').format(dateFormat);
          dateEnd = date.startOf('year').add(5, 'month').endOf('month').format(dateFormat);
        } else {
          dateStart = date.startOf('year').add(6, 'month').format(dateFormat);
          dateEnd = date.endOf('year').format(dateFormat);
        }
        periodLength = 2;
      }

      const data = {
        engine: `api${urls.engines}/${engine.id}`,
        dateStart,
        dateEnd,
        periodLength,
      };

      await addPeriod(data);

      message.success(t('report.added'));
      mutatePeriods();
      form.resetFields();
    } catch (errors: any) {
      if (errors['hydra:description']) {
        message.error(errors['hydra:description']);
      }
    } finally {
      setIsPeriodCreating(false);
    }
  };

  const disabledDate = (current: Dayjs | null, info: { from?: Dayjs }) => {
    if (!current) return false;

    /*if (current.isBefore(today, 'day') || current.isSame(today, 'day')) {
      return true;
    }*/

    const startDate = info.from;
    if (!startDate) return false;

    if (current.isBefore(startDate, 'day')) {
      return true;
    }

    if (isWeekInterval) {
      if (current.isBefore(startDate.add(7, 'day'), 'day')) {
        return true;
      }
    } else {
      const daysInMonth = startDate.daysInMonth();
      const endOfPeriod = startDate.add(daysInMonth, 'day');

      if (current.isBefore(endOfPeriod, 'day')) {
        return true;
      }
    }

    return false;
  };

  const disabledQuarterDate = (current: Dayjs | null): boolean => {
    if (!current) return false;
    //const quarterStart = current.startOf('quarter');
    //return quarterStart.isBefore(today, 'day') || quarterStart.isSame(today, 'day');
    return false;
  };

  const disabledSemesterDate = (current: Dayjs | null): boolean => {
    if (!current) return false;

   // return current.isBefore(today, 'day');
    return false;
  };

  return (
    <Form form={form} layout="vertical">
      <Flex vertical gap={10}>
        <Flex gap={10} justify="space-between" align="flex-end">
          <Form.Item
            name="period"
            rules={[
              {
                required: true,
                message: t('common.choose'),
              },
            ]}
          >
            {periodType === 'custom' ? (
              <RangePicker
                size="middle"
                placeholder={[t('list.dateStart'), t('list.dateEnd')]}
                format={t('common.dateFormat')}
                disabled={!Roles.hasAccess('canCreateEnginePeriod')}
                style={{ width: '100%' }}
                disabledDate={disabledDate}
                allowClear
              />
            ) : periodType === 'quarter' ? (
              <DatePicker
                size="middle"
                picker="quarter"
                format={(date) => {
                  if (!date) return '';
                  const quarter = Math.floor(date.month() / 3) + 1;
                  return `Q${quarter} ${date.year()}`;
                }}
                disabled={!Roles.hasAccess('canCreateEnginePeriod')}
                style={{ width: '100%', minWidth: '200px' }}
                disabledDate={disabledQuarterDate}
                allowClear
              />
            ) : (
              <HalfYearPicker
                onChange={(date, dateString) => console.log(date, dateString)}
                placeholder="Выберите полугодие"
                disabledDate={disabledSemesterDate}
              />
            )}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <ButtonBlack
              loading={isPeriodCreating}
              disabled={!Roles.hasAccess('canCreateEnginePeriod')}
              onClick={createPeriod}
              htmlType="button"
            >
              {t('info.createPlan')}
            </ButtonBlack>
          </Form.Item>
        </Flex>

        <Form.Item style={{ marginBottom: 0 }}>
          <Radio.Group
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value)}
            disabled={!Roles.hasAccess('canCreateEnginePeriod')}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="custom">{t('info.customDataPeriod')}</Radio.Button>
            <Radio.Button value="quarter">{t('info.quarter')}</Radio.Button>
            <Radio.Button value="semester">{t('info.semester')}</Radio.Button>
          </Radio.Group>
        </Form.Item>
      </Flex>
    </Form>
  );
}
